import Button from "@/components/bricks/button/Button";
import CloseIcon from "../../../icons/CloseIcon";
import EditIcon from "../../../icons/EditIcon";
import { DisplayItem } from "../../../formcontrols/DisplayItem";
import { TextInputFormik } from "../../../formcontrols/InputItem";
import { useLinjeoplysninger } from "./formik/useLinjeoplysninger";
import {
  ApolloQueryResult,
  OperationVariables,
} from "@apollo/client/core/types";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../../ErrorFallback";
import { useNavigate } from "react-router-dom";
import AutoCompleteFormControl from "../../../formcontrols/AutoCompleteFormControl";
import { useHotels } from "../../../../hooks/useQueries";
import StamopIcon from "../../../icons/StamopIcon";
import { Fmulinje } from "../../../../interfaces/schema";
import { useContext, useState } from "react";
import { UserQueryContext } from "../../../../context/UserContext";

const Linjeoplysninger = ({
  fmulinje,
  refetch,
}: {
  fmulinje: Fmulinje;
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      fmulinje: Fmulinje;
    }>
  >;
}) => {
  const [editMode, setEditMode] = useState(false);
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "KursusWrite");
  const { hotels, getHotelById, getHotelsError, getHotelsLoading } =
    useHotels();
  const navigate = useNavigate();
  const formik = useLinjeoplysninger(fmulinje, setEditMode, refetch);
  const { initialValues, handleSubmit, isSubmitting } = formik;
  return (
    <div className={`c-card c-card--editable${editMode ? " is-editable" : ""}`}>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => navigate("/linje")}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
          <h3 className="d-flex align-items-center gap-2">
            <span className="c-theme-icon bg-primary-light text-primary">
              <StamopIcon height={26} />
            </span>{" "}
            Stamoplysninger
          </h3>
          {canWrite && (
            <Button
              type="button"
              transparent
              color="primary"
              onClick={() => {
                setEditMode(!editMode);
              }}
              aria-label={editMode ? "Luk" : "Redigér"}
              className="btn btn-primary btn--transparent edit-trigger"
            >
              <>
                {editMode && (
                  <>
                    Luk <CloseIcon />{" "}
                  </>
                )}
                {!editMode && (
                  <>
                    {" "}
                    Redigér <EditIcon />{" "}
                  </>
                )}
              </>
            </Button>
          )}
        </div>
        <div className="row gx-5 gy-5">
          <div className="col-12 col-xl-12">
            <div className="c-item-list">
              {editMode && (
                <div className="flex-25">
                  <TextInputFormik
                    formik={formik}
                    config={{
                      component: "textinput",
                      id: "navn",
                      initialValue: initialValues.navn ?? "",
                      label: "Linjenavn",
                    }}
                  />
                </div>
              )}
              {!editMode && (
                <div className="flex-25">
                  <DisplayItem
                    label="Linjenavn"
                    text={fmulinje.navn ?? ""}
                    isEditable={true}
                  />
                </div>
              )}
              {editMode && (
                // Tilføj autocomplete som ved kursusoplysninger
                <div className="flex-25">
                  <label className="form-label" htmlFor={"hotelId"}>
                    Hotel/sted
                  </label>
                  <AutoCompleteFormControl
                    onChange={(e) => {
                      const selectedHotel = getHotelById(e);

                      if (selectedHotel) {
                        formik.setFieldValue("hotelId", selectedHotel.id);
                      } else {
                        formik.setFieldValue("hotelId", undefined);
                      }
                    }}
                    queryValues={{
                      data: hotels
                        ? hotels.map((h) => {
                            return {
                              ...h,
                              navn: h.navn1,
                            };
                          })
                        : undefined,
                      error: getHotelsError,
                      isPending: getHotelsLoading,
                    }}
                    value={formik.values.hotelId}
                  />
                </div>
              )}
              {!editMode && (
                <div className="flex-25">
                  <DisplayItem
                    label="Hotel"
                    text={getHotelById(fmulinje.hotelId)?.navn1 ?? "Ukendt"}
                    isEditable={true}
                  />
                </div>
              )}
              {/* <div className="flex-25 todo">
                <DisplayItem
                  label="Antal kursister"
                  text={"Afventer"}
                  isEditable={false}
                />
              </div> */}
            </div>
          </div>

          {editMode && (
            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <Button
                color="secondary"
                className="me-md-2"
                onClick={() => {
                  setEditMode(!editMode);
                }}
              >
                Annuller
              </Button>
              <Button
                color="primary"
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                Gem
              </Button>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default Linjeoplysninger;
